import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import { Grid, Box, Typography, IconButton, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Swal from "sweetalert2";
import { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../../../api";

const ImageDropZone = ({ file, onFileSelect, onFileRemove, label }) => {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    // If the file is already a URL string (from edit mode)
    if (typeof file === "string") {
      setPreview(file);
      return;
    }
    // If the file is a File object
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);

    // Free memory when component unmounts or file changes
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <Box
      sx={{
        border: "2px dashed #ccc",
        borderRadius: "8px",
        padding: "16px",
        textAlign: "center",
        position: "relative",
        bgcolor: "#fafafa",
        cursor: "pointer",
        transition: "all 0.3s ease",
        height: "180px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        "&:hover": {
          borderColor: "#1976d2",
          bgcolor: "#f4f9ff",
          boxShadow: "0 4px 12px rgba(25, 118, 210, 0.1)",
        },
      }}
      component="label"
    >
      {preview ? (
        <>
          <img
            src={preview}
            alt={label}
            style={{
              maxHeight: "100%",
              maxWidth: "100%",
              objectFit: "contain",
              borderRadius: "4px"
            }}
          />
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              onFileRemove();
            }}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(255, 255, 255, 0.8)",
              "&:hover": { bgcolor: "rgba(255, 255, 255, 1)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          <CloudUploadIcon sx={{ fontSize: 40, color: "#9e9e9e", mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Click or drag to upload {label}
          </Typography>
        </>
      )}
      <input
        type="file"
        hidden
        accept="image/*"
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) {
            onFileSelect(e.target.files[0]);
          }
        }}
      />
    </Box>
  );
};

export default function TierLevelDialog({ open, close, getTierLevels, editData }) {
  const token = localStorage.getItem("token");
  const url = API_URL();
  const api = axios.create({
    baseURL: url,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const [levelName, setLevelName] = useState("");
  const [icon, setIcon] = useState(null);
  const [description, setDescription] = useState("");
  const [bgImage, setBgImage] = useState(null);
  const [levelNumber, setLevelNumber] = useState("");
  const [entryLevel, setEntryLevel] = useState(false);
  const [entryMinPoint, setEntryMinPoint] = useState("");
  const [minPointRange, setMinPointRange] = useState("");
  const [maxPointRange, setMaxPointRange] = useState("");

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editData) {
      setLevelName(editData.levelName || "");
      setIcon(editData.icon || null);
      setDescription(editData.description || "");
      setBgImage(editData.bgImage || null);
      setLevelNumber(editData.levelNumber || "");
      setEntryLevel(editData.entryLevel ?? false);
      setEntryMinPoint(editData.entryMinPoint || "");
      setMinPointRange(editData.minPointRange || "");
      setMaxPointRange(editData.maxPointRange || "");
    } else {
      resetForm();
    }
  }, [editData, open]);

  const resetForm = () => {
    setLevelName("");
    setIcon(null);
    setDescription("");
    setBgImage(null);
    setLevelNumber("");
    setEntryLevel(false);
    setEntryMinPoint("");
    setMinPointRange("");
    setMaxPointRange("");
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    close();
  };

  const onSubmit = (e) => {
    e.preventDefault();
    let required = {};
    if (!levelName) required.levelName = "Level Name is required";
    if (!levelNumber) required.levelNumber = "Level Number is required";
    if (entryLevel && entryMinPoint === "") required.entryMinPoint = "Entry Min Point is required";

    if (Object.keys(required).length > 0) {
      setErrors(required);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("levelName", levelName);
    formData.append("description", description);
    formData.append("levelNumber", Number(levelNumber));
    formData.append("entryLevel", entryLevel);
    formData.append("entryMinPoint", Number(entryMinPoint));
    formData.append("minPointRange", Number(minPointRange));
    formData.append("maxPointRange", Number(maxPointRange));

    if (icon instanceof File) {
      formData.append("icon", icon);
    } else if (icon === null) {
      formData.append("icon", "");
    } else if (typeof icon === "string") {
      // It's likely an existing URL, no need to upload unless API requires it.
    }

    if (bgImage instanceof File) {
      formData.append("bgImage", bgImage);
    } else if (bgImage === null) {
      formData.append("bgImage", "");
    }

    const axiosConfig = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };

    if (editData) {
      api.put(`tier-levels/${editData._id}`, formData, axiosConfig)
        .then(function (response) {
          Swal.fire({
            title: "Successful!",
            text: "Tier Level Updated Successfully!",
            icon: "success",
            cancelButtonText: "Close",
          });
          getTierLevels();
          handleClose();
        })
        .catch(function (err) {
          Swal.fire({
            title: "Something Went Wrong!",
            text: err?.response?.data?.message || "Error updating tier level",
            icon: "error",
            cancelButtonText: "Close",
          });
        })
        .finally(() => setIsSubmitting(false));
    } else {
      api.post("tier-levels", formData, axiosConfig)
        .then(function (response) {
          Swal.fire({
            title: "Successful!",
            text: "Tier Level Created Successfully!",
            icon: "success",
            cancelButtonText: "Close",
          });
          getTierLevels();
          handleClose();
        })
        .catch(function (err) {
          Swal.fire({
            title: "Something Went Wrong!",
            text: err?.response?.data?.message || "Error creating tier level",
            icon: "error",
            cancelButtonText: "Close",
          });
        })
        .finally(() => setIsSubmitting(false));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: "bold", borderBottom: "1px solid #eee", mb: 2, fontSize: "1.25rem", color: "#333" }}>
        {editData ? "Update Tier Level" : "Create Tier Level"}
      </DialogTitle>
      <DialogContent sx={{ pt: "20px !important" }}>
        <form onSubmit={onSubmit} id="tier-level-form">
          <Grid container spacing={4}>
            {/* Left Column: Form Details */}
            <Grid item xs={12} md={7}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Level Name"
                    variant="outlined"
                    fullWidth
                    value={levelName}
                    onChange={(e) => setLevelName(e.target.value)}
                    error={!!errors?.levelName}
                    helperText={errors?.levelName}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Level Number"
                    type="number"
                    variant="outlined"
                    fullWidth
                    value={levelNumber}
                    onChange={(e) => setLevelNumber(e.target.value)}
                    error={!!errors?.levelNumber}
                    helperText={errors?.levelNumber}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Entry Level"
                    select
                    variant="outlined"
                    fullWidth
                    value={entryLevel}
                    onChange={(e) => {
                      const isEntry = e.target.value === "true" || e.target.value === true;
                      setEntryLevel(isEntry);
                      if (!isEntry) {
                        setEntryMinPoint("");
                        setErrors((prev) => ({ ...prev, entryMinPoint: undefined }));
                      }
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  >
                    <MenuItem value={true}>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                  </TextField>
                </Grid>
                {entryLevel && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      label="Entry Min Point"
                      type="number"
                      variant="outlined"
                      fullWidth
                      value={entryMinPoint}
                      onChange={(e) => setEntryMinPoint(e.target.value)}
                      error={!!errors?.entryMinPoint}
                      helperText={errors?.entryMinPoint}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Min Point Range"
                    type="number"
                    variant="outlined"
                    fullWidth
                    value={minPointRange}
                    onChange={(e) => setMinPointRange(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Max Point Range"
                    type="number"
                    variant="outlined"
                    fullWidth
                    value={maxPointRange}
                    onChange={(e) => setMaxPointRange(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Description"
                    multiline
                    rows={3}
                    variant="outlined"
                    fullWidth
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Right Column: Media Uploads */}
            <Grid item xs={12} md={5}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}>
                    Tier Icon
                  </Typography>
                  <ImageDropZone
                    file={icon}
                    onFileSelect={setIcon}
                    onFileRemove={() => setIcon(null)}
                    label="Icon"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}>
                    Background Image
                  </Typography>
                  <ImageDropZone
                    file={bgImage}
                    onFileSelect={setBgImage}
                    onFileRemove={() => setBgImage(null)}
                    label="Background Image"
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2.5, borderTop: "1px solid #eee", bgcolor: "#f8f9fa", borderBottomLeftRadius: 8, borderBottomRightRadius: 8 }}>
        <Button onClick={handleClose} disabled={isSubmitting} color="inherit" sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", px: 3 }}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="tier-level-form"
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          sx={{ textTransform: "none", fontWeight: 600, px: 4, borderRadius: "8px", boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)", "&:hover": { boxShadow: "0 6px 16px rgba(25, 118, 210, 0.3)" } }}
        >
          {editData ? "Update Tier" : "Create Tier"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}